import type { Car } from '@multirx/shared';
import { createCar } from './api.js';
import { createPillButton } from '../ui/pillButton.js';
import {
  normalizeGarageCarName,
  MAX_GARAGE_CAR_NAME_LENGTH,
} from '../garage/garageEntityRename.js';

export type CreateCarModal = {
  open: () => void;
  close: () => void;
};

/**
 * Monte la modale « Nouvelle voiture » (nom → POST /api/cars).
 * @param host - Élément parent dans lequel la modale est injectée
 * @param onCreated - Callback appelé avec la voiture créée après succès
 * @returns Contrôleur `{ open, close }`
 */
export function mountCreateCarModal(
  host: HTMLElement,
  onCreated: (car: Car) => void
): CreateCarModal {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop drivers-create-modal';
  backdrop.hidden = true;
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-labelledby', 'car-create-modal-title');

  backdrop.innerHTML = `
    <div class="modal drivers-create-modal-dialog">
      <div class="modal-header">
        <h2 id="car-create-modal-title">Nouvelle voiture</h2>
        <button type="button" class="btn btn-ghost drivers-create-modal-close" aria-label="Fermer">×</button>
      </div>
      <form class="modal-body drivers-create-modal-body" id="car-create-form">
        <div class="field brutalist-container">
          <input class="brutalist-input" type="text" id="car-create-name" name="name" placeholder="NOM" required maxlength="${MAX_GARAGE_CAR_NAME_LENGTH}" autocomplete="off" />
          <label class="brutalist-label" for="car-create-name">Nom</label>
        </div>
        <p class="drivers-create-status" id="car-create-status" role="alert" hidden></p>
      </form>
      <div class="modal-footer drivers-create-modal-footer"></div>
    </div>`;

  host.appendChild(backdrop);

  const form = backdrop.querySelector('#car-create-form') as HTMLFormElement;
  const status = backdrop.querySelector('#car-create-status') as HTMLElement;
  const nameInput = backdrop.querySelector('#car-create-name') as HTMLInputElement;
  const footer = backdrop.querySelector('.drivers-create-modal-footer') as HTMLElement;

  const submitBtn = createPillButton('Créer', { variant: 'cta' });
  footer.appendChild(submitBtn);
  const btn = submitBtn as HTMLButtonElement;

  const resetStatus = (): void => {
    status.hidden = true;
    status.textContent = '';
  };

  const close = (): void => {
    backdrop.hidden = true;
    resetStatus();
  };

  const open = (): void => {
    resetStatus();
    form.reset();
    backdrop.hidden = false;
    nameInput.focus();
  };

  backdrop.querySelector('.drivers-create-modal-close')?.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  backdrop.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  const submit = (): void => {
    resetStatus();
    const name = normalizeGarageCarName(nameInput.value);
    if (!name) {
      status.hidden = false;
      status.textContent = `Entrez un nom (1–${MAX_GARAGE_CAR_NAME_LENGTH} caractères).`;
      return;
    }
    btn.disabled = true;
    void createCar({ name, engine: 2, tire: 2 })
      .then((car) => {
        close();
        onCreated(car);
      })
      .catch((err: unknown) => {
        status.hidden = false;
        status.textContent = err instanceof Error ? err.message : 'Création impossible.';
      })
      .finally(() => {
        btn.disabled = false;
      });
  };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    submit();
  });
  btn.addEventListener('click', () => form.requestSubmit());

  return { open, close };
}
